import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import authService from '../services/authService';
import useTenant from '../hooks/useTenant';

export const Register: React.FC = () => {
  const { tenantName, logoUrl } = useTenant();
  const navigate = useNavigate();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('teacher');
  const [employeeId, setEmployeeId] = useState('');

  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!firstName || !lastName || !email || !password) {
      setError('Please fill in all required fields.');
      return;
    }

    setError(null);
    setSuccess(null);
    setSubmitting(true);

    try {
      const response = await authService.register({
        first_name: firstName,
        last_name: lastName,
        email,
        password,
        role,
        employee_id: employeeId || undefined
      });

      if (response.success) {
        setSuccess('Teacher account created successfully! Redirecting to login page...');
        setTimeout(() => {
          navigate('/login');
        }, 2500);
      } else {
        setError(response.message || 'Registration failed.');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Server error. Please verify your connection details.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4 relative overflow-hidden">
      {/* Decorative background shapes */}
      <div className="absolute -top-40 -right-40 w-96 h-96 rounded-full bg-indigo-100 blur-3xl opacity-60"></div>
      <div className="absolute -bottom-40 -left-40 w-96 h-96 rounded-full bg-pink-100 blur-3xl opacity-60"></div>
      
      <div className="bg-white border border-slate-200 rounded-3xl shadow-xl w-full max-w-lg p-8 relative z-10 space-y-6">
        
        {/* Branding header */}
        <div className="text-center space-y-2">
          {logoUrl ? (
            <img src={logoUrl} alt={tenantName} className="h-12 w-auto mx-auto object-contain" />
          ) : (
            <div className="h-12 w-12 rounded-2xl bg-indigo-600 flex items-center justify-center text-white text-xl font-black mx-auto">
              {tenantName ? tenantName[0] : 'S'}
            </div>
          )}
          <h1 className="text-page-header tracking-tight text-slate-900">{tenantName || 'School Management Portal'}</h1>
          <p className="text-helper text-slate-500">Create your teacher credential profile</p>
        </div>

        {error && (
          <div className="p-3 bg-rose-50 border border-rose-200 text-rose-700 rounded-xl text-center text-helper">
            ⚠️ {error}
          </div>
        )}

        {success && (
          <div className="p-3 bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-xl text-center text-helper font-semibold">
            💚 {success}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-form-label uppercase tracking-wider block">First Name</label>
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="John"
                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-table-body focus:border-indigo-500 focus:bg-white outline-none transition-all"
                required
                disabled={submitting}
              />
            </div>
            <div className="space-y-1">
              <label className="text-form-label uppercase tracking-wider block">Last Name</label>
              <input
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Manda"
                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-table-body focus:border-indigo-500 focus:bg-white outline-none transition-all"
                required
                disabled={submitting}
              />
            </div>
          </div>

          <div className="space-y-1">
            <label className="text-form-label uppercase tracking-wider block">Email Address</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="teacher@school.edu"
              className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-table-body focus:border-indigo-500 focus:bg-white outline-none transition-all"
              required
              disabled={submitting}
            />
          </div>

          <div className="space-y-1">
            <label className="text-form-label uppercase tracking-wider block">Security Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-table-body focus:border-indigo-500 focus:bg-white outline-none transition-all"
              required
              disabled={submitting}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-form-label uppercase tracking-wider block">Employee ID</label>
              <input
                type="text"
                value={employeeId}
                onChange={(e) => setEmployeeId(e.target.value)}
                placeholder="EMP102"
                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-table-body focus:border-indigo-500 focus:bg-white outline-none transition-all"
                disabled={submitting}
              />
            </div>
            <div className="space-y-1">
              <label className="text-form-label uppercase tracking-wider block">Appointed Role</label>
              <select 
                value={role} 
                onChange={(e) => setRole(e.target.value)}
                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-table-body focus:border-indigo-500 focus:bg-white outline-none transition-all h-[38px]"
                disabled={submitting}
              >
                <option value="teacher">Teacher</option>
                <option value="head_teacher">Head Teacher</option>
              </select>
            </div>
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-3 rounded-xl transition-all shadow-md hover:shadow-lg disabled:opacity-50 text-nav flex items-center justify-center gap-2 pt-2.5"
          >
            {submitting ? (
              <>
                <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></span>
                <span>Creating Account...</span>
              </>
            ) : (
              <span>Create Account</span>
            )}
          </button>
        </form>

        <div className="text-center pt-4 border-t border-slate-100 text-helper text-slate-500">
          Already have an account? <Link to="/login" className="text-indigo-600 font-bold hover:underline">Sign In here &rarr;</Link>
        </div>

      </div>
    </div>
  );
};

export default Register;
